import { useState, useEffect, useMemo, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart } from "@/components/shopping-cart";
import { GradeBadge } from "@/components/grade-badge";
import { Sparkles } from "lucide-react";
import { FACTORY_REFURBISHED_LABEL, FACTORY_REFURBISHED_GRADING_ANCHOR, getDisplayBrand, isFactoryRefurbished } from "@shared/palletDisplay";
import { VideoPlayer } from "@/components/video-player";
import { VideoThumbnailCard } from "@/components/VideoThumbnailCard";
import { useCartStore } from "@/stores/cart-store";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ArrowLeft, Minus, Plus, Video, ArrowRight, ChevronLeft, ChevronRight, X, Filter, MapPin, AlertCircle, Star, Download, FileText, ChevronDown, Info, Package, Wrench, Bell, TrendingUp, Clock, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { optimizedSrcSet } from "@/lib/imageOptimize";
import { ImageGallery } from "@/components/image-gallery";
import { PalletCompositionCard, PalletCompositeHero, PalletPackagingExample } from "@/components/pallet-composition-card";
import { ProductFeaturesAndSpecs } from "@/components/product-features-specs";
import { parsePrice, formatMoney } from "@shared/currency";
import { useReferencePriceLabel, useHidePalletPackaging, useShowSpecifications, useGradeBadgeCategory } from "@/lib/category-meta";
import type { GradeBadgeCategory } from "@/components/grade-badge";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { normalizeVideoUrl } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { trackViewItem, trackAddToCart } from "@/lib/analytics";
import { getShipDateColor } from "@/lib/shipDateColors";
import { useShipDateColorMap } from "@/hooks/useShipDateColorMap";

interface CartConflict {
  removed: Array<{ cartItemId: string; modelId: string; modelName: string; brand: string; imageUrl?: string; grade: string; quantity: number; serialNumber?: string; warehouseCode?: string; warehouseLocation?: string; reason: string }>;
  adjusted: Array<{ cartItemId: string; modelId: string; modelName: string; brand: string; imageUrl?: string; grade: string; oldQuantity: number; newQuantity: number; warehouseCode?: string; warehouseLocation?: string; reason: string }>;
}

// Component to show inventory-level cart popularity ("X people have this in their cart")
function InventoryPopularity({ inventoryId }: { inventoryId: string }) {
  const { data } = useQuery<{ totalCarts: number; totalQuantity: number }>({
    queryKey: [`/api/cart/inventory-popularity/${inventoryId}`],
    staleTime: 60000,
    refetchInterval: 300000,
  });

  if (!data || data.totalCarts === 0) return null;

  return (
    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800" data-testid={`cart-popularity-${inventoryId}`}>
      <TrendingUp className="h-3 w-3 text-orange-600 dark:text-orange-400" />
      <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
        {data.totalCarts} {data.totalCarts === 1 ? 'person has' : 'people have'} this in cart
      </span>
    </div>
  );
}

/**
 * ConditionBadge — renders the appropriate "what's the cosmetic condition?" pill
 * for an inventory row. For virtual pallet products (`isPallet=true`) this is a
 * "Factory Refurbished" pill; for everything else it falls through to the
 * existing GradeBadge with the unit's A/B+/B- open-box grade.
 *
 * Pallet inventory is stored with grade="B" for legacy compatibility but the
 * units themselves are factory-refurbished, NOT generic open-box B-grade. This
 * presentation-only re-label avoids touching the importer/inventory pipeline.
 */
function ConditionBadge({
  isFactoryRefurbished: isFR,
  grade,
  price,
  compact,
  category,
}: {
  /** Drives the FR pill — derived from shared/palletDisplay.isFactoryRefurbished(model)
   *  rather than raw model.isPallet, so future non-refurbished pallet programs
   *  (e.g. microwave bundles) won't accidentally inherit the green FR pill. */
  isFactoryRefurbished?: boolean;
  grade: "A" | "B+" | "B-";
  price?: number;
  compact?: boolean;
  /** Per-category copy + pallet-pill metadata (Task #156). */
  category?: GradeBadgeCategory | null;
}) {
  if (isFR) {
    return (
      <Link
        href={FACTORY_REFURBISHED_GRADING_ANCHOR}
        className={`inline-flex items-center gap-1 font-semibold rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 transition-colors cursor-pointer ${compact ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-1'}`}
        data-testid="badge-factory-refurbished"
        title="Factory-refurbished by the manufacturer to like-new spec — click to learn more"
      >
        <Sparkles className="h-3 w-3" />
        {FACTORY_REFURBISHED_LABEL}
      </Link>
    );
  }
  // Forward price to GradeBadge ONLY for pallet categories — pallet rows
  // render the inline "[icon] Pallet · $price" pill (Task #156). On
  // non-pallet rows we keep the legacy grade-letter badge unchanged
  // (price stays in the existing right-aligned column).
  const palletPrice = category?.saleType === "pallets" ? price : undefined;
  return <GradeBadge grade={grade} price={palletPrice} compact={compact} category={category} />;
}

export default function ProductDetail() {
  const [, params] = useRoute("/product/:id");
  const [location, navigate] = useLocation();
  const modelId = params?.id;
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [openGrades, setOpenGrades] = useState<string[]>([]);
  const { addItem, removeItem, updateQuantity, items: cartItems, setHighlightedModel } = useCartStore();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [cartConflicts, setCartConflicts] = useState<CartConflict | null>(null);
  const [showStockNotificationDialog, setShowStockNotificationDialog] = useState(false);
  const [notificationDefaultGrade, setNotificationDefaultGrade] = useState<string>('Any');

  // Function to open notification dialog with a specific grade
  const openNotificationDialogWithGrade = (grade: string) => {
    setNotificationDefaultGrade(grade);
    setShowStockNotificationDialog(true);
  };

  // Filter state management - supports all grade types (A, B, B+, B-)
  const [activeFilters, setActiveFilters] = useState<{
    warehouseCodes: string[];
    grades: string[];
  }>({
    warehouseCodes: [],
    grades: [],
  });

  // Sync filters FROM URL (initial load and when URL changes)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const warehousesFromUrl = searchParams.get('warehouses')?.split(',').filter(Boolean) || [];
    const gradesFromUrl = searchParams.get('grades')?.split(',').filter(Boolean) || [];
    
    setActiveFilters({
      warehouseCodes: warehousesFromUrl,
      grades: gradesFromUrl,
    });
  }, [location]);

  // Sync filters TO URL when filters change manually (via UI)
  useEffect(() => {
    // Guard against undefined modelId during navigation transitions
    if (!modelId) return;
    
    const params = new URLSearchParams();
    if (activeFilters.warehouseCodes.length > 0) {
      params.set('warehouses', activeFilters.warehouseCodes.join(','));
    }
    if (activeFilters.grades.length > 0) {
      params.set('grades', activeFilters.grades.join(','));
    }
    // Preserve the originating page (e.g. /category/refrigerators?brand=LG)
    // so the "Back to Products" link can return there even after the user
    // changes filters on the PDP.
    const fromParam = new URLSearchParams(window.location.search).get('from');
    if (fromParam) {
      params.set('from', fromParam);
    }
    const newUrl = `/product/${modelId}${params.toString() ? '?' + params.toString() : ''}`;
    if (location !== newUrl) {
      navigate(newUrl, { replace: true });
    }
  }, [activeFilters, modelId, location, navigate]);

  const addFilter = (type: 'warehouse' | 'grade', value: string) => {
    setActiveFilters(prev => {
      if (type === 'warehouse') {
        return { ...prev, warehouseCodes: [...prev.warehouseCodes, value] };
      } else {
        return { ...prev, grades: [...prev.grades, value] };
      }
    });
  };

  const removeFilter = (type: 'warehouse' | 'grade', value: string) => {
    setActiveFilters(prev => {
      if (type === 'warehouse') {
        return { ...prev, warehouseCodes: prev.warehouseCodes.filter(w => w !== value) };
      } else {
        return { ...prev, grades: prev.grades.filter(g => g !== value) };
      }
    });
  };

  const clearAllFilters = () => {
    setActiveFilters({ warehouseCodes: [], grades: [] });
  };

  // Cart conflict checking function
  const checkCartConflicts = async () => {
    if (!isAuthenticated || cartItems.length === 0) {
      return;
    }

    try {
      const result = await apiRequest('POST', '/api/cart/conflicts', {}) as CartConflict;

      if (result.removed.length > 0 || result.adjusted.length > 0) {
        setCartConflicts(result);
        await queryClient.invalidateQueries({ queryKey: ['/api/cart'] });
      }
    } catch (error) {
      // Session can expire between page loads — the request 401s with
      // "No active session found". That's an expected state for a guest
      // (or a user whose cookie just lapsed), not a real failure, so we
      // silently ignore 401s instead of spamming the error reporter.
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.startsWith('401')) return;
      console.error('Failed to check cart conflicts:', error);
    }
  };

  // Auto-check cart availability on page load
  useEffect(() => {
    checkCartConflicts();
  }, []);

  // Check if current model is in cart
  const isModelInCart = cartItems.some(item => item.modelId === modelId);

  const { data: model, isLoading: modelLoading } = useQuery<any>({
    queryKey: ['/api/models', modelId],
    enabled: !!modelId,
  });

  // These two hooks must run unconditionally on every render — they live here
  // (above all early returns) so the hook order stays stable while `model` is
  // still loading. They safely accept undefined and return defaults until the
  // model and the categories list resolve.
  const referencePriceLabel = useReferencePriceLabel((model as any)?.categoryId);
  const hidePackaging = useHidePalletPackaging((model as any)?.categoryId);
  // Per-category opt-out of the Specifications section (rich Dimensions /
  // Product Details cards + the Basic Specifications fallback). Defaults to
  // true so categories that don't set the flag keep showing specs.
  const showSpecifications = useShowSpecifications((model as any)?.categoryId);
  // Per-category grading copy + pallet-pill metadata (Task #156). Resolved
  // from /api/categories so the inventory-card grade pill, its tooltip, and
  // its per-grade detail line are all admin-configurable per category.
  const gradeBadgeCategory = useGradeBadgeCategory((model as any)?.categoryId);

  const { data: inventory, isLoading: inventoryLoading } = useQuery<any[]>({
    queryKey: ['/api/inventory/model', modelId],
    enabled: !!modelId,
  });

  const { data: serializedUnits, isLoading: unitsLoading } = useQuery<any[]>({
    queryKey: ['/api/serialized-units', selectedUnit?.inventoryId],
    enabled: !!selectedUnit?.inventoryId && selectedUnit?.gradeHandlingMode === 'serial',
  });

  const { data: cartPopularity } = useQuery<{ totalCarts: number; recentActivity: number }>({
    queryKey: [`/api/cart/popular/${modelId}`],
    enabled: !!modelId,
    staleTime: 60000,
  });

  // Fetch "Coming Soon" inventory (pre-arrival items from manifests)
  const { data: comingSoonData } = useQuery<{ items: Array<{
    id: string;
    serialNumber: string;
    grade: string;
    quantity: number;
    expectedShipDate: string | null;
    warehouseCode: string | null;
    warehouseLocation: string | null;
    videoUrl: string | null;
    serializedUnitId: string | null;
    isComingSoon: boolean;
  }> }>({
    queryKey: [`/api/inventory/coming-soon/${modelId}`],
    enabled: !!modelId,
  });

  const comingSoonItems = comingSoonData?.items || [];

  // Shared ship-date → color-slot map sourced from /api/inventory/upcoming-ship-dates
  // (future dates only, same sorted order in all views for consistent color assignment)
  const shipDateColorMap = useShipDateColorMap();

  // Track view_item event when model data is loaded
  const viewItemTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (model && modelId && viewItemTrackedRef.current !== modelId) {
      viewItemTrackedRef.current = modelId;
      
      // Calculate starting price from inventory if available
      const startingPrice = inventory?.reduce((min: number, item: any) => {
        const price = parsePrice(item.price);
        return price > 0 && price < min ? price : min;
      }, Infinity) || parsePrice(model.msrp) || 0;
      
      trackViewItem(
        {
          id: modelId,
          modelNumber: model.mfgSku,
          brand: model.brand,
          category: model.category,
          price: startingPrice === Infinity ? 0 : startingPrice,
        },
        startingPrice === Infinity ? 0 : startingPrice
      );
    }
  }, [model, modelId, inventory]);

  // Fetch sorted model IDs for navigation
  const { data: sortedModelIds } = useQuery<string[]>({
    queryKey: ['/api/models/sorted'],
  });

  // Compute low-inventory grade IDs that should auto-expand (2 or fewer units)
  const lowInventoryGradeIds = useMemo(() => {
    if (!inventory || inventoryLoading) return [];
    
    const autoExpandIds: string[] = [];
    
    inventory.forEach((inventoryItem: any) => {
      const gradeAccordionValue = `grade-${inventoryItem.id}`;
      const isQuantityMode = inventoryItem.gradeHandlingMode === 'quantity';
      
      if (isQuantityMode) {
        // Quantity mode: check remaining quantity after cart deductions
        const inCart = cartItems
          .filter(item => item.inventoryId === inventoryItem.id)
          .reduce((sum, item) => sum + item.quantity, 0);
        const availableCount = Math.max(0, inventoryItem.quantity - inCart);
        
        if (availableCount > 0 && availableCount <= 2) {
          autoExpandIds.push(gradeAccordionValue);
        }
      } else {
        // Serial mode: check remaining available units not in cart
        // A unit is available if: not shipped AND not allocated
        const serializedUnits = inventoryItem.serializedUnits || [];
        const availableUnits = serializedUnits.filter((unit: any) => 
          !unit.isShipped && !unit.allocatedOrderItemId && !cartItems.some(item => item.unitId === unit.id)
        );
        
        if (availableUnits.length > 0 && availableUnits.length <= 2) {
          autoExpandIds.push(gradeAccordionValue);
        }
      }
    });
    
    return autoExpandIds;
  }, [inventory, inventoryLoading, cartItems]);

  // Sync openGrades with auto-expand list while preserving manual selections
  useEffect(() => {
    if (inventoryLoading) return;
    
    // Merge manual selections with auto-open list
    const autoSet = new Set(lowInventoryGradeIds);
    const currentSet = new Set(openGrades);
    
    // Keep grades that are either:
    // 1. Auto-expandable (low inventory)
    // 2. Manually opened by user (in current openGrades but may not have low inventory)
    const mergedIds = Array.from(new Set([
      ...lowInventoryGradeIds, // Always include auto-expand grades
      ...openGrades.filter(id => {
        // Keep manually-opened grades even if they don't have low inventory
        // But remove them if they no longer exist in inventory
        return inventory?.some((inv: any) => `grade-${inv.id}` === id) || autoSet.has(id);
      })
    ]));
    
    // Only update if the set actually changed
    const currentSortedArray = Array.from(currentSet).sort();
    const mergedSortedArray = Array.from(new Set(mergedIds)).sort();
    const currentSorted = currentSortedArray.join(',');
    const mergedSorted = mergedSortedArray.join(',');
    
    if (currentSorted !== mergedSorted) {
      setOpenGrades(mergedIds);
    }
  }, [lowInventoryGradeIds, openGrades, inventoryLoading, inventory]);

  // Get current product position and navigation info
  const currentIndex = sortedModelIds?.findIndex(id => id === modelId) ?? -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (sortedModelIds?.length ?? 0) - 1;
  const prevModelId = hasPrev ? sortedModelIds?.[currentIndex - 1] : null;
  const nextModelId = hasNext ? sortedModelIds?.[currentIndex + 1] : null;

  // Build filter query string for navigation links
  const filterQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (activeFilters.warehouseCodes.length > 0) {
      params.set('warehouses', activeFilters.warehouseCodes.join(','));
    }
    if (activeFilters.grades.length > 0) {
      params.set('grades', activeFilters.grades.join(','));
    }
    return params.toString();
  }, [activeFilters]);

  // Build navigation URLs with filter params. If we arrived from a category
  // (or other browse page), honor `?from=` so the user returns to that exact
  // listing+filter state instead of being dumped on the showroom landing.
  const fromParam = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('from');
  const backHref = fromParam
    ? fromParam
    : (filterQueryString ? `/?${filterQueryString}` : '/');
  // Compose query for prev/next/first/last so the originating page (`from`)
  // and filter state both survive product-to-product navigation.
  const navQueryString = (() => {
    const params = new URLSearchParams(filterQueryString);
    if (fromParam) params.set('from', fromParam);
    const s = params.toString();
    return s ? `?${s}` : '';
  })();
  const firstProductUrl = sortedModelIds?.[0]
    ? `/product/${sortedModelIds[0]}${navQueryString}`
    : '';
  const prevProductUrl = prevModelId
    ? `/product/${prevModelId}${navQueryString}`
    : '';
  const nextProductUrl = nextModelId
    ? `/product/${nextModelId}${navQueryString}`
    : '';
  const lastProductUrl = sortedModelIds?.[sortedModelIds.length - 1]
    ? `/product/${sortedModelIds[sortedModelIds.length - 1]}${navQueryString}`
    : '';

  // Preprocess inventory into visible/hidden warehouses with zero-inventory filtering
  const { visibleWarehouses, hiddenWarehouses, filteredOutCount } = useMemo(() => {
    if (!inventory || inventory.length === 0) {
      return { visibleWarehouses: [], hiddenWarehouses: [], filteredOutCount: 0 };
    }

    // Get cart warehouse IDs for prioritization
    const cartWarehouseIds = new Set(
      cartItems
        .filter(item => item.modelId === modelId)
        .map(item => {
          const inv = inventory?.find((i: any) => i.id === item.inventoryId);
          return inv?.addressId || 'no-warehouse';
        })
    );

    // Group inventory by warehouse (include all grades, even out of stock)
    const warehouseGroups = new Map<string, any[]>();
    inventory.forEach((inventoryItem: any) => {
      // Calculate available quantity based on grade handling mode (quantity vs serial)
      let availableCount = 0;
      let totalUnsoldCount = 0;
      let inCartCount = 0;
      
      // Use gradeHandlingMode from API - 'quantity' means aggregate, 'serial' means individual units
      const isQuantityMode = inventoryItem.gradeHandlingMode === 'quantity';
      
      if (isQuantityMode) {
        // Quantity mode: check quantity field (used for A-grade refrigerators, all Johnson Fitness grades, etc.)
        inCartCount = cartItems
          .filter(item => item.inventoryId === inventoryItem.id)
          .reduce((sum, item) => sum + item.quantity, 0);
        totalUnsoldCount = inventoryItem.quantity || 0;
        availableCount = Math.max(0, totalUnsoldCount - inCartCount);
      } else {
        // Serial mode: check for available units tracked individually
        // Serial mode inventory is tracked in serializedUnits, not via quantity field
        // A unit is available if: not shipped AND not allocated
        const unsoldUnits = inventoryItem.serializedUnits?.filter((unit: any) => !unit.isShipped && !unit.allocatedOrderItemId) || [];
        const unsoldUnitIdSet = new Set(unsoldUnits.map((u: any) => u.id as string));
        // Only count cart items whose unitId is actually a physical unit in this inventory's serializedUnits.
        // Presale (pre-arrival) items share the same inventoryId but their unitId is not in serializedUnits,
        // so they must not inflate inCartCount or trigger the "all in cart" collapse.
        inCartCount = cartItems.filter(item =>
          item.unitId && unsoldUnitIdSet.has(item.unitId)
        ).length;
        totalUnsoldCount = unsoldUnits.length;
        availableCount = Math.max(0, totalUnsoldCount - inCartCount);
      }

      // Distinguish between "all in my cart" vs "truly out of stock"
      const allInCart = availableCount === 0 && inCartCount > 0 && totalUnsoldCount > 0;
      const isOutOfStock = availableCount === 0 && !allInCart;

      // Include all grades (even out of stock) - mark availability
      const warehouseKey = inventoryItem.addressId || 'no-warehouse';
      if (!warehouseGroups.has(warehouseKey)) {
        warehouseGroups.set(warehouseKey, []);
      }
      warehouseGroups.get(warehouseKey)!.push({
        ...inventoryItem,
        _availableCount: availableCount,
        _inCartCount: inCartCount,
        _allInCart: allInCart,
        _isOutOfStock: isOutOfStock
      });
    });

    // Split warehouses into visible and hidden based on filters
    const visible: any[] = [];
    const hidden: any[] = [];
    
    // Check if any filters are active
    const filtersActive = activeFilters.warehouseCodes.length > 0 || activeFilters.grades.length > 0;

    warehouseGroups.forEach((warehouseInventory, warehouseKey) => {
      // Filter out out-of-stock grades — only show grades that have stock or are in cart
      const inStockInventory = warehouseInventory.filter((item: any) => !item._isOutOfStock);

      // Skip this warehouse entirely if nothing is in stock (and nothing in cart)
      if (inStockInventory.length === 0) return;

      const firstItem = inStockInventory[0];
      const warehouseCode = firstItem.address?.warehouseCode || 'N/A';
      const isInCart = cartWarehouseIds.has(warehouseKey);

      // If NO filters active, show everything
      if (!filtersActive) {
        visible.push({ warehouseKey, warehouseInventory: inStockInventory, isInCart, firstItem });
        return;
      }

      // Filters are active - apply filtering logic
      const warehouseMatches = activeFilters.warehouseCodes.length === 0 || 
                              activeFilters.warehouseCodes.includes(warehouseCode);

      // Product detail page always shows all grades - no filtering by URL grade params
      const visibleGrades = inStockInventory;
      const hiddenGrades: any[] = [];

      if (warehouseMatches && visibleGrades.length > 0) {
        visible.push({ warehouseKey, warehouseInventory: visibleGrades, isInCart, firstItem });
      } else if (!warehouseMatches || visibleGrades.length === 0) {
        hidden.push({ warehouseKey, warehouseInventory: hiddenGrades.length > 0 ? hiddenGrades : inStockInventory, isInCart, firstItem });
      }
    });

    // Sort visible warehouses: cart warehouses first
    visible.sort((a, b) => {
      if (a.isInCart && !b.isInCart) return -1;
      if (!a.isInCart && b.isInCart) return 1;
      return 0;
    });

    const filteredCount = hidden.reduce((sum, h) => sum + h.warehouseInventory.length, 0);

    return {
      visibleWarehouses: visible,
      hiddenWarehouses: hidden,
      filteredOutCount: filteredCount
    };
  }, [inventory, cartItems, modelId, activeFilters]);

  // Set of serialized_unit IDs already in the cart — used to show "In Cart" state on presale cards
  const presaleUnitIdsInCart = useMemo(
    () => new Set(cartItems.map(i => i.unitId).filter(Boolean) as string[]),
    [cartItems]
  );

  useEffect(() => {
    document.title = "OpenBox by ShopJimmy";
  }, [model]);

  // Scroll to top whenever modelId changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [modelId]);

  // Set highlighted model when page loads or modelId changes
  useEffect(() => {
    if (modelId) {
      setHighlightedModel(modelId);
    }
    return () => {
      setHighlightedModel(null); // Clear on unmount
    };
  }, [modelId, setHighlightedModel]);

  // Reset grade accordion states when modelId changes
  useEffect(() => {
    setOpenGrades([]);
  }, [modelId]);

  if (!modelId) {
    return <div>Model not found</div>;
  }

  const isLoading = modelLoading || inventoryLoading;

  const handleQuantityChange = (inventoryId: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [inventoryId]: Math.max(1, (prev[inventoryId] || 1) + delta)
    }));
  };

  const handleAddToCart = (inventoryItem: any, unit?: any) => {
    if (!model || !inventoryItem) return;
    
    const isQuantityMode = inventoryItem.gradeHandlingMode === 'quantity';

    // For quantity-mode items, check available stock before adding
    if (isQuantityMode) {
      const inCart = cartItems
        .filter(item => item.inventoryId === inventoryItem.id)
        .reduce((sum, item) => sum + item.quantity, 0);
      const availableCount = Math.max(0, inventoryItem.quantity - inCart);
      const requestedQty = quantities[inventoryItem.id] || 1;

      if (availableCount === 0) {
        toast({
          title: "All available units are in your cart",
          description: "Want to be notified when more become available?",
          action: (
            <ToastAction
              altText="Notify me when back in stock"
              onClick={() => openNotificationDialogWithGrade(inventoryItem.grade)}
              data-testid="toast-notify-me-button"
            >
              <Bell className="h-3 w-3 mr-1" />
              Notify Me
            </ToastAction>
          ),
        });
        return;
      }

      if (requestedQty > availableCount) {
        toast({
          title: "Insufficient Stock",
          description: `Only ${availableCount} unit${availableCount === 1 ? '' : 's'} available. Adjust quantity or request notification for more.`,
          variant: "destructive",
        });
        return;
      }
    }

    const cartItem = {
      id: unit ? `${inventoryItem.id}-${unit.id}` : `${inventoryItem.id}-${Date.now()}`,
      modelId: model.id,
      inventoryId: inventoryItem.id,
      unitId: unit?.id,
      brand: displayBrand,
      modelName: model.mfgSku,
      description: model.description,
      msrp: model.msrp,
      grade: inventoryItem.grade,
      quantity: isQuantityMode ? (quantities[inventoryItem.id] || 1) : 1,
      price: parsePrice(inventoryItem.sellingPrice || '0'), // Parse decimal price from API
      serialNumber: unit?.serialNumber,
      videoUrl: unit?.videoUrl,
      dimensions: {
        width: model.dimensionsWidth || 36,
        depth: model.dimensionsDepth || 34,
      },
      imageUrl: model.imageUrl,
      warehouseCode: inventoryItem.address?.warehouseCode,
      city: inventoryItem.address?.city,
      region: inventoryItem.address?.region,
      isPallet: model.isPallet,
      isFactoryRefurbished: isFactoryRefurbished(model),
      categoryId: (model as any).categoryId ?? null,
    };

    addItem(cartItem);

    // Track add to cart event
    const quantity = isQuantityMode ? (quantities[inventoryItem.id] || 1) : 1;
    const price = parsePrice(inventoryItem.sellingPrice || '0');
    trackAddToCart(
      {
        id: model.id,
        modelNumber: model.mfgSku,
        brand: displayBrand,
        category: model.category,
        grade: inventoryItem.grade,
        price,
      },
      quantity,
      price * quantity
    );

    // Reset quantity for quantity-mode items
    if (isQuantityMode) {
      setQuantities(prev => ({ ...prev, [inventoryItem.id]: 1 }));
    }
  };

  type ComingSoonItem = NonNullable<typeof comingSoonData>['items'][number];
  interface InventoryRecord { grade: string; sellingPrice?: string | null; address?: { warehouseCode?: string | null } | null; }

  /**
   * Add a pre-arrival (coming-soon) unit to cart.
   * Looks up price from any matching inventory record by grade.
   * optionalInventoryItem — when called from inside an inventory section,
   * the parent inventoryItem is passed directly so we don't need to search.
   */
  const handleAddToCartPresale = (comingSoonItem: ComingSoonItem, optionalInventoryItem?: InventoryRecord) => {
    if (!model) return;

    // Prevent duplicate — check by serial number or unitId
    const alreadyInCart = cartItems.some(
      ci => ci.serialNumber === comingSoonItem.serialNumber ||
            (comingSoonItem.serializedUnitId && ci.unitId === comingSoonItem.serializedUnitId)
    );
    if (alreadyInCart) {
      toast({ title: "Already in cart", description: "This unit is already in your cart." });
      return;
    }

    // Resolve price from explicitly-passed inventoryItem or any matching grade in the inventory list
    const inventoryItem: InventoryRecord | undefined = optionalInventoryItem ||
      (inventory as InventoryRecord[] | undefined)?.find(inv => inv.grade === comingSoonItem.grade);

    const clientPrice = inventoryItem ? parsePrice(inventoryItem.sellingPrice || '0') : 0;

    // Block only when we have neither a unitId (for server-side price resolution) nor a client price
    if (!comingSoonItem.serializedUnitId && clientPrice === 0) {
      toast({ title: "Price unavailable", description: "No pricing found for this unit. Please contact an admin.", variant: "destructive" });
      return;
    }
    const unitPrice = clientPrice; // Server overrides price when unitId is present
    const formattedDate = comingSoonItem.expectedShipDate
      ? format(parseISO(comingSoonItem.expectedShipDate), 'MMM d, yyyy')
      : null;

    const cartItem = {
      id: `presale-${comingSoonItem.id}`,
      modelId: model.id,
      inventoryId: inventoryItem?.id,
      unitId: comingSoonItem.serializedUnitId || undefined,
      brand: displayBrand,
      modelName: model.mfgSku,
      description: model.description,
      msrp: model.msrp,
      grade: comingSoonItem.grade,
      quantity: 1,
      price: unitPrice,
      serialNumber: comingSoonItem.serialNumber,
      videoUrl: comingSoonItem.videoUrl || undefined,
      expectedShipDate: comingSoonItem.expectedShipDate || undefined,
      dimensions: {
        width: model.dimensionsWidth || 36,
        depth: model.dimensionsDepth || 34,
      },
      imageUrl: model.imageUrl,
      warehouseCode: comingSoonItem.warehouseCode || inventoryItem?.address?.warehouseCode,
      city: inventoryItem?.address?.city,
      region: inventoryItem?.address?.region,
      isPallet: model.isPallet,
      isFactoryRefurbished: isFactoryRefurbished(model),
      categoryId: (model as any).categoryId ?? null,
    } satisfies CartItem;

    addItem(cartItem);
    toast({
      title: "Pre-arrival unit reserved",
      description: formattedDate ? `Estimated arrival: ${formattedDate}` : "Item reserved",
    });
    trackAddToCart(
      { id: model.id, modelNumber: model.mfgSku, brand: displayBrand, category: model.category, grade: comingSoonItem.grade, price: unitPrice },
      1,
      unitPrice
    );
  };

  const handleSelectUnit = (unit: any, inventoryItem: any) => {
    // Normalize storage paths / Dropbox share links via the shared helper
    const videoUrl = normalizeVideoUrl(unit.videoUrl) ?? unit.videoUrl;

    setSelectedUnit({ 
      ...unit, 
      videoUrl, 
      grade: inventoryItem.grade, 
      inventoryId: inventoryItem.id 
    });
    setIsVideoModalOpen(true);
  };

  if (isLoading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-6 w-32 mb-4" />
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-96 mb-8" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <Skeleton className="h-64 w-full" />
          </div>
          <div className="lg:col-span-5">
            <Skeleton className="h-96 w-full mb-6" />
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="lg:col-span-4">
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </section>
    );
  }

  if (!model) {
    return <div className="text-center py-12">Product not found</div>;
  }

  const displayBrand = getDisplayBrand(model);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {cartConflicts && (cartConflicts.removed.length > 0 || cartConflicts.adjusted.length > 0) && (
        <Dialog open={true} onOpenChange={(open) => !open && setCartConflicts(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-cart-conflicts">
            <button
              onClick={() => setCartConflicts(null)}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              data-testid="button-close-conflicts-x"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Cart Items No Longer Available
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Some items in your cart are no longer available at the selected quantities. We've automatically updated your cart:
              </p>

              {cartConflicts.removed.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-destructive">Removed Items:</h3>
                  <div className="space-y-3">
                    {cartConflicts.removed.map((item) => (
                      <Alert key={item.cartItemId} variant="destructive" className="relative">
                        <div className="flex gap-3">
                          {item.imageUrl ? (
                            <picture>
                              <source
                                type="image/webp"
                                srcSet={optimizedSrcSet(item.imageUrl, [200, 400]) || undefined}
                                sizes="64px"
                              />
                              <img
                                src={item.imageUrl}
                                alt={item.modelName}
                                loading="lazy"
                                decoding="async"
                                className="w-16 h-16 object-contain rounded flex-shrink-0"
                              />
                            </picture>
                          ) : (
                            <Package className="h-16 w-16 p-3 text-muted-foreground flex-shrink-0 bg-muted rounded" />
                          )}
                          <div className="flex-1">
                            <AlertTitle className="font-medium">
                              {item.brand} {item.modelName} - Grade {item.grade}
                              {item.serialNumber && ` (${item.serialNumber})`}
                            </AlertTitle>
                            <AlertDescription className="text-sm mt-1">
                              <div>Quantity: {item.quantity}</div>
                              {item.warehouseCode && item.warehouseLocation && (
                                <div className="flex items-center gap-1 mt-1 text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {item.warehouseCode} | {item.warehouseLocation}
                                </div>
                              )}
                              <div className="mt-1 italic">{item.reason}</div>
                            </AlertDescription>
                          </div>
                        </div>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}

              {cartConflicts.adjusted.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-yellow-600 dark:text-yellow-500">Quantity Adjusted:</h3>
                  <div className="space-y-3">
                    {cartConflicts.adjusted.map((item) => (
                      <Alert key={item.cartItemId} className="border-yellow-600 dark:border-yellow-500 relative">
                        <div className="flex gap-3">
                          {item.imageUrl ? (
                            <picture>
                              <source
                                type="image/webp"
                                srcSet={optimizedSrcSet(item.imageUrl, [200, 400]) || undefined}
                                sizes="64px"
                              />
                              <img
                                src={item.imageUrl}
                                alt={item.modelName}
                                loading="lazy"
                                decoding="async"
                                className="w-16 h-16 object-contain rounded flex-shrink-0"
                              />
                            </picture>
                          ) : (
                            <Package className="h-16 w-16 p-3 text-muted-foreground flex-shrink-0 bg-muted rounded" />
                          )}
                          <div className="flex-1">
                            <AlertTitle className="font-medium">
                              {item.brand} {item.modelName} - Grade {item.grade}
                            </AlertTitle>
                            <AlertDescription className="text-sm mt-1">
                              <div>Original: {item.oldQuantity} → New: {item.newQuantity}</div>
                              {item.warehouseCode && item.warehouseLocation && (
                                <div className="flex items-center gap-1 mt-1 text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {item.warehouseCode} | {item.warehouseLocation}
                                </div>
                              )}
                              <div className="mt-1 italic">{item.reason}</div>
                            </AlertDescription>
                          </div>
                        </div>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                onClick={() => setCartConflicts(null)} 
                className="w-full"
                data-testid="button-close-conflicts"
              >
                OK, Got It
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <div className="container-responsive">
        <div className="mb-4 text-center">
          {/* Navigation - Mobile: stack, Desktop: row */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <Link href={backHref} className="text-primary hover:underline inline-flex items-center gap-2 text-sm sm:text-base touch-target">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Products</span>
              <span className="sm:hidden">Back</span>
            </Link>
            {/* Product navigation - simplified on mobile */}
            <div className="flex gap-1 sm:gap-2 w-full sm:w-auto justify-center sm:justify-end">
              {currentIndex > 0 && sortedModelIds ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(firstProductUrl)}
                  className="hidden sm:inline-flex touch-target"
                  data-testid="button-first-product"
                >
                  &laquo;
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled className="hidden sm:inline-flex">
                  &laquo;
                </Button>
              )}
              {prevModelId ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(prevProductUrl)}
                  className="touch-target px-2 sm:px-3"
                  data-testid="button-prev-product"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled className="px-2 sm:px-3">
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
              )}
              {nextModelId ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(nextProductUrl)}
                  className="touch-target px-2 sm:px-3"
                  data-testid="button-next-product"
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled className="px-2 sm:px-3">
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
              {currentIndex >= 0 && currentIndex < (sortedModelIds?.length ?? 0) - 1 && sortedModelIds ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(lastProductUrl)}
                  className="hidden sm:inline-flex touch-target"
                  data-testid="button-last-product"
                >
                  &raquo;
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled className="hidden sm:inline-flex">
                  &raquo;
                </Button>
              )}
            </div>
          </div>
          {/* Product title - responsive sizing */}
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2" data-testid="product-title">
            {displayBrand} {model.mfgSku} {model.msrp && <><span style={{color: '#9eba4a'}} className="hidden sm:inline">|</span><span style={{color: '#65758b'}} className="hidden sm:inline"> {referencePriceLabel}: </span></>}<span className="line-through text-muted-foreground text-base sm:text-lg md:text-xl">{model.msrp ? formatMoney(parsePrice(model.msrp)) : ''}</span>
          </h2>
          {model.rating && model.reviewsCount && (
            <div className="flex items-center justify-center gap-1 sm:gap-2 mb-2 flex-wrap" data-testid="product-rating">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3 w-3 sm:h-4 sm:w-4 ${
                      i < Math.floor(parseFloat(model.rating))
                        ? 'fill-yellow-400 text-yellow-400'
                        : i < Math.ceil(parseFloat(model.rating))
                        ? 'fill-yellow-400/50 text-yellow-400'
                        : 'fill-none text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="font-semibold text-foreground text-sm sm:text-base">{parseFloat(model.rating).toFixed(1)}</span>
              <span className="text-muted-foreground text-xs sm:text-sm">({model.reviewsCount.toLocaleString()} reviews)</span>
            </div>
          )}
          {cartPopularity && cartPopularity.recentActivity > 0 && (
            <div className="flex items-center justify-center gap-2 mb-2" data-testid="cart-activity-indicator">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800">
                <TrendingUp className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                <span className="text-xs sm:text-sm font-medium text-orange-700 dark:text-orange-300">
                  {cartPopularity.recentActivity} {cartPopularity.recentActivity === 1 ? 'person added' : 'people added'} this to cart in the last 24 hours
                </span>
              </div>
            </div>
          )}
          <p className="font-bold text-sm sm:text-base md:text-[18px] text-[#3c444f] px-2 sm:px-0">{model.description}</p>
        </div>

        <div className="three-column-layout">
        {/* LEFT COLUMN: Image Gallery - Full width mobile, 3 cols desktop */}
        {/* Mobile: order-1 (first), Desktop: normal */}
        <div className="lg:col-span-3 lg:sticky top-4 self-start order-1 lg:order-none">
          <Card className="overflow-hidden" data-testid="product-display">
            <CardContent className="p-0">
              <div data-testid="product-image-view">
                {model.isPallet ? (
                  <PalletCompositeHero
                    palletModelId={model.id}
                    alt={`${model.brand} ${model.mfgSku}`}
                    heroImageUrl={(model as any).palletHeroImageUrl ?? null}
                  />
                ) : (
                  <ImageGallery 
                    images={[model.imageUrl, ...(model.additionalImages || [])]}
                    alt={`${model.brand} ${model.mfgSku}`}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {model.isPallet && !hidePackaging && (
            <div className="mt-3">
              <PalletPackagingExample />
            </div>
          )}

          <div
            className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-muted/40 border border-border/60 text-xs text-muted-foreground leading-relaxed"
            data-testid="product-data-accuracy-disclaimer"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <p>
              Images, specs, and accessory lists come from suppliers or AI-assisted
              discovery and are believed accurate but not guaranteed. We encourage you
              to research the model number — and you're welcome to{' '}
              <a
                href="mailto:openbox@shopjimmy.com?subject=Schedule%20an%20in-person%20inspection"
                className="underline hover:text-foreground"
                data-testid="link-inspect-in-person"
              >
                inspect units in person
              </a>
              .{' '}
              <Link
                href="/terms#data-accuracy"
                className="underline hover:text-foreground"
                data-testid="link-data-accuracy-terms"
              >
                Learn more
              </Link>
              .
            </p>
          </div>


          {/* Stock Notification Button - Desktop only (hidden on mobile, shown after inventory) */}
          <Button
            onClick={() => openNotificationDialogWithGrade('Any')}
            className="hidden lg:inline-flex items-center justify-center gap-2 rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 px-4 w-full hover:bg-[#45a049] text-white font-semibold py-8 h-auto whitespace-normal bg-[#9eba4a] touch-target text-[18px] pt-[0px] pb-[0px] pl-[5px] pr-[5px] mt-[20px] mb-[20px]"
            data-testid="button-stock-notification"
          >
            <Bell className="h-5 w-5 mr-2 flex-shrink-0" />
            Request Additional Quantity
          </Button>

          {/* Need Replacement Parts Button - Desktop only (hidden on mobile, shown after inventory) */}
          <a
            href={`https://www.shopjimmy.com/search.php?search_query=${encodeURIComponent(model.brand + ' ' + model.mfgSku)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block mt-3"
            data-testid="link-view-parts-catalog"
          >
            <Button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 px-4 w-full bg-[#546931] hover:bg-[#45a049] text-white font-semibold py-6 touch-target pl-[5px] pr-[5px] pt-[0px] pb-[0px] text-[16px]"
              data-testid="button-view-parts-catalog"
            >
              <Wrench className="h-5 w-5 mr-2 flex-shrink-0" />
              View Available Parts
            </Button>
          </a>

          {/* Filter Badges UI - Only show warehouse filters on product page, grade filters don't apply here */}
          {activeFilters.warehouseCodes.length > 0 && (
            <div className="mt-3 p-3 bg-secondary/20 rounded-md" data-testid="filter-badges-section">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {activeFilters.warehouseCodes.length} filter{activeFilters.warehouseCodes.length !== 1 ? 's' : ''} active:
                </span>
                
                {activeFilters.warehouseCodes.map(code => (
                  <Badge 
                    key={`warehouse-${code}`} 
                    variant="secondary" 
                    className="gap-1 pr-1"
                    data-testid={`filter-badge-warehouse-${code}`}
                  >
                    <MapPin className="h-3 w-3" />
                    {code}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${code} warehouse filter`}
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => removeFilter('warehouse', code)}
                      data-testid={`button-remove-filter-warehouse-${code}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
                
                {activeFilters.warehouseCodes.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={clearAllFilters}
                    data-testid="button-clear-all-filters"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* MIDDLE COLUMN: Warehouse Selection - Full width mobile */}
        {/* Mobile: order-2 (after images), Desktop: normal */}
        <div className="lg:col-span-5 order-2 lg:order-none mb-4 lg:mb-0">
          {/* Render Visible Warehouses - Each in its own Card */}
          {visibleWarehouses.map(({ warehouseKey, warehouseInventory, isInCart, firstItem }) => {
            const warehouseCode = firstItem.address?.warehouseCode || 'N/A';
            const city = firstItem.address?.city || '';
            const region = firstItem.address?.region || '';
            const warehouseLabel = firstItem.address 
              ? `${warehouseCode} | ${city}, ${region}`
              : 'Warehouse Not Assigned';

            return (
              <Card key={warehouseKey} className="overflow-hidden mb-3" data-testid={`warehouse-card-${warehouseCode}`}>
                <CardHeader 
                  className="py-2 px-2 sm:px-3"
                  style={{backgroundColor: '#9eba4a', color: '#000000'}}
                >
                  <CardTitle className="text-sm sm:text-base font-semibold truncate">{warehouseLabel}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="px-0 pb-0">
                          {/* Grades within this warehouse */}
                          {warehouseInventory.map((inventoryItem: any) => {
                            const gradeAccordionValue = `grade-${inventoryItem.id}`;
                            const isOpen = openGrades.includes(gradeAccordionValue);
                            const isQuantityMode = inventoryItem.gradeHandlingMode === 'quantity';

                            // Quantity mode: Show quantity selector (A-grade refrigerators, all Johnson Fitness grades, etc.)
                            if (isQuantityMode) {
                              const isOutOfStock = inventoryItem._isOutOfStock;
                              const allInCart = inventoryItem._allInCart;
                              
                              // All units in cart - show blue "In Cart" message
                              if (allInCart) {
                                return (
                                  <div 
                                    key={inventoryItem.id}
                                    className="border-b border-border last:border-b-0 bg-blue-50 dark:bg-blue-950/20"
                                  >
                                    <div className="px-2 sm:px-4 py-2 sm:py-3">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-1 sm:gap-3">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                          <ConditionBadge isFactoryRefurbished={isFactoryRefurbished(model)} grade={inventoryItem.grade} price={parsePrice(inventoryItem.sellingPrice)} compact={true} category={gradeBadgeCategory} />
                                          {!model.isPallet && (
                                            <span className="font-medium text-foreground text-sm sm:text-base">
                                              Class {inventoryItem.grade}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                                          <span className="font-semibold text-foreground">
                                            {formatMoney(parsePrice(inventoryItem.sellingPrice || '0'))}
                                          </span>
                                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                                            {inventoryItem._inCartCount} In Cart
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Truly out of stock A-grade: hide entirely
                              if (isOutOfStock) {
                                return null;
                              }
                              
                              return (
                                <div 
                                  key={inventoryItem.id}
                                  className="border-b border-border last:border-b-0"
                                >
                                  {/* A Grade Header - Always Visible - Mobile responsive */}
                                  <div className="px-2 sm:px-4 py-2 sm:py-3 bg-background">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-1 sm:gap-3">
                                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                        <ConditionBadge isFactoryRefurbished={isFactoryRefurbished(model)} grade={inventoryItem.grade} price={parsePrice(inventoryItem.sellingPrice)} compact={true} category={gradeBadgeCategory} />
                                        {!model.isPallet && (
                                          <span className="font-medium text-foreground text-sm sm:text-base">
                                            Class {inventoryItem.grade}
                                          </span>
                                        )}
                                        {inventoryItem.boxType && (
                                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full" data-testid={`badge-box-type-${inventoryItem.id}`}>
                                            {inventoryItem.boxType}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                                        <span className="font-semibold text-foreground">
                                          {formatMoney(parsePrice(inventoryItem.sellingPrice || '0'))}
                                        </span>
                                        <span className="text-muted-foreground">
                                          {(() => {
                                            const inCart = cartItems
                                              .filter(item => item.inventoryId === inventoryItem.id)
                                              .reduce((sum, item) => sum + item.quantity, 0);
                                            return `${Math.max(0, inventoryItem.quantity - inCart)} avail`;
                                          })()}
                                        </span>
                                      </div>
                                    </div>
                                    {/* Per-arrival-date batch breakdown.
                                        Splits the row's available units by their effective ETA
                                        (in stock now vs. each upcoming arrival date) so customers
                                        can see "1 in stock, 1 arriving May 4, 2 arriving May 7"
                                        instead of a single collapsed count.
                                        Cart subtractions are applied oldest-first (in-stock first)
                                        because fulfillment allocates oldest units first. */}
                                    {(() => {
                                      const batches = (inventoryItem.batches || []) as Array<{ key: string; label: string; eta: string | null; availableCount: number }>;
                                      if (batches.length === 0) return null;
                                      const inCartTotal = cartItems
                                        .filter((item: any) => item.inventoryId === inventoryItem.id)
                                        .reduce((sum: number, item: any) => sum + item.quantity, 0);
                                      let remaining = inCartTotal;
                                      const adjusted = batches.map(b => {
                                        const drained = Math.min(remaining, b.availableCount);
                                        remaining -= drained;
                                        return { ...b, availableCount: b.availableCount - drained };
                                      }).filter(b => b.availableCount > 0);
                                      if (adjusted.length === 0) return null;

                                      // Render a friendly short label per batch:
                                      //   "1 in stock", "1 arriving May 4", "2 arriving May 7"
                                      const shortLabel = (b: { key: string; eta: string | null }) => {
                                        if (b.key === 'in-stock') return 'in stock';
                                        if (!b.eta) return 'date pending';
                                        const d = new Date(b.eta);
                                        return `arriving ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
                                      };

                                      // Single en-route batch: show a compact inline caption only.
                                      if (adjusted.length === 1 && adjusted[0].key !== 'in-stock') {
                                        return (
                                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-400" data-testid={`batch-summary-${inventoryItem.id}`}>
                                            All {shortLabel(adjusted[0])}
                                          </div>
                                        );
                                      }

                                      // Multiple batches: stacked breakdown as colored pills.
                                      if (adjusted.length > 1) {
                                        return (
                                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`batch-breakdown-${inventoryItem.id}`}>
                                            {adjusted.map(b => (
                                              <span
                                                key={b.key}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                                                  b.key === 'in-stock'
                                                    ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                                                    : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                                                }`}
                                                data-testid={`batch-${inventoryItem.id}-${b.key}`}
                                              >
                                                {b.key === 'in-stock'
                                                  ? <ShieldCheck className="h-3 w-3" />
                                                  : <Clock className="h-3 w-3" />
                                                }
                                                <span className="font-medium">{b.availableCount}</span>
                                                <span>{shortLabel(b)}</span>
                                              </span>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    <InventoryPopularity inventoryId={inventoryItem.id} />
                                  </div>

                                  {/* Quantity Mode Content - Always Visible - Mobile responsive */}
                                  <div className="px-2 sm:px-4 pb-2 sm:pb-3">
                                    {(() => {
                                      const inCart = cartItems
                                        .filter(item => item.inventoryId === inventoryItem.id)
                                        .reduce((sum, item) => sum + item.quantity, 0);
                                      const availableCount = Math.max(0, inventoryItem.quantity - inCart);
                                      const currentQty = quantities[inventoryItem.id] || 1;
                                      const inCartItems = cartItems.filter(item => item.inventoryId === inventoryItem.id);

                                      if (availableCount === 0 && inventoryItem.quantity > 0) {
                                        return (
                                          <div className="text-center py-2 sm:py-3 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                                            <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-medium">
                                              All available units in your cart
                                            </p>
                                          </div>
                                        );
                                      }

                                      return (
                                        <div data-testid={`grade-a-section-${inventoryItem.id}`}>
                                          {/* Quantity controls - responsive for mobile */}
                                          <div className="flex items-center justify-center gap-1 sm:gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              aria-label="Remove last from cart"
                                              className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-muted-foreground hover:text-foreground touch-target"
                                              onClick={() => {
                                                if (inCartItems.length > 0) {
                                                  const lastItem = inCartItems[inCartItems.length - 1];
                                                  if (lastItem.quantity > 1) {
                                                    updateQuantity(lastItem.id, lastItem.quantity - 1);
                                                  } else {
                                                    removeItem(lastItem.id);
                                                  }
                                                }
                                              }}
                                              disabled={inCartItems.length === 0}
                                              data-testid={`button-remove-from-cart-${inventoryItem.id}`}
                                            >
                                              <ArrowLeft className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              aria-label="Decrease quantity"
                                              className="h-8 w-8 sm:h-9 sm:w-9 p-0 touch-target"
                                              onClick={() => handleQuantityChange(inventoryItem.id, -1)}
                                              disabled={currentQty <= 1}
                                              data-testid={`button-decrease-${inventoryItem.id}`}
                                            >
                                              <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                                            </Button>
                                            <input
                                              type="number"
                                              value={currentQty}
                                              onChange={(e) => setQuantities(prev => ({
                                                ...prev,
                                                [inventoryItem.id]: Math.max(1, Math.min(availableCount, parseInt(e.target.value) || 1))
                                              }))}
                                              className="w-12 sm:w-16 h-8 sm:h-9 px-2 sm:px-3 py-1 sm:py-2 border border-border rounded-md text-center bg-background text-sm sm:text-base"
                                              min="1"
                                              max={availableCount}
                                            />
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              aria-label="Increase quantity"
                                              className="h-8 w-8 sm:h-9 sm:w-9 p-0 touch-target"
                                              onClick={() => handleQuantityChange(inventoryItem.id, 1)}
                                              disabled={currentQty >= availableCount}
                                              data-testid={`button-increase-${inventoryItem.id}`}
                                            >
                                              <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              aria-label="Add to cart"
                                              className="bg-primary text-primary-foreground hover:bg-primary/90 h-7 w-7 sm:h-8 sm:w-8 p-0 touch-target"
                                              onClick={() => handleAddToCart(inventoryItem)}
                                              disabled={availableCount === 0}
                                              data-testid={`button-add-to-cart-${inventoryItem.id}`}
                                            >
                                              <ArrowRight className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              );
                            }

                            // Serial mode grades: Keep accordion wrapper (unless out of stock or all in cart)
                            const isOutOfStock = inventoryItem._isOutOfStock;
                            const allInCart = inventoryItem._allInCart;

                            // Compute coming-soon items for this grade/warehouse BEFORE the allInCart
                            // early return so we can decide whether to collapse or keep showing presale cards.
                            // Also filter out presale units already in the cart — they disappear from the list.
                            const currentWarehouseCode = inventoryItem.address?.warehouseCode;
                            const gradeComingSoonItems = comingSoonItems.filter((item: any) =>
                              item.grade === inventoryItem.grade &&
                              (!currentWarehouseCode || item.warehouseCode === currentWarehouseCode) &&
                              !(item.serializedUnitId && presaleUnitIdsInCart.has(item.serializedUnitId))
                            );

                            // All physical units in cart AND no presale units left to add - show "In Cart" banner
                            if (allInCart && gradeComingSoonItems.length === 0) {
                              return (
                                <div 
                                  key={inventoryItem.id}
                                  className="border-b border-border last:border-b-0 bg-blue-50 dark:bg-blue-950/20"
                                >
                                  <div className="px-2 sm:px-4 py-2 sm:py-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-1 sm:gap-3">
                                      <div className="flex items-center gap-2 sm:gap-3">
                                        <ConditionBadge isFactoryRefurbished={isFactoryRefurbished(model)} grade={inventoryItem.grade} price={parsePrice(inventoryItem.sellingPrice)} compact={true} category={gradeBadgeCategory} />
                                        {!model.isPallet && (
                                          <span className="font-medium text-foreground text-sm sm:text-base">
                                            Class {inventoryItem.grade}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                                        <span className="font-semibold text-foreground">
                                          {formatMoney(parsePrice(inventoryItem.sellingPrice || '0'))}
                                        </span>
                                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                                          {inventoryItem._inCartCount} In Cart
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            // Truly out of stock - hide entirely
                            if (isOutOfStock) {
                              return null;
                            }
                            
                            return (
                              <div key={inventoryItem.id}>
                                <Accordion 
                                  type="multiple" 
                                  value={openGrades}
                                  onValueChange={setOpenGrades}
                                >
                                  <AccordionItem 
                                    value={gradeAccordionValue}
                                    className="border-b border-border last:border-b-0"
                                  >
                                  <AccordionTrigger className="px-3 sm:px-4 py-3 sm:py-3 hover:bg-secondary/50">
                                    <div className="flex items-center justify-between w-full pr-2">
                                      <div className="flex items-center gap-3 sm:gap-3 flex-wrap">
                                        <ConditionBadge isFactoryRefurbished={isFactoryRefurbished(model)} grade={inventoryItem.grade} price={parsePrice(inventoryItem.sellingPrice)} compact={false} category={gradeBadgeCategory} />
                                        {!model.isPallet && (
                                          <span className="hidden sm:inline font-medium text-foreground">
                                            Class {inventoryItem.grade}
                                          </span>
                                        )}
                                        {inventoryItem.boxType && (
                                          <span className="hidden sm:inline text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full" data-testid={`badge-box-type-b-${inventoryItem.id}`}>
                                            {inventoryItem.boxType}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 sm:gap-4 text-base sm:text-sm">
                                        <span className="font-bold sm:font-semibold text-foreground text-lg sm:text-sm">
                                          {formatMoney(parsePrice(inventoryItem.sellingPrice || '0'))}
                                        </span>
                                        <span className="text-muted-foreground font-medium sm:font-normal">
                                          {`${inventoryItem.serializedUnits?.filter((unit: any) => !unit.isShipped && !unit.allocatedOrderItemId && !cartItems.some(item => item.unitId === unit.id)).length || 0} avail`}
                                        </span>
                                      </div>
                                    </div>
                                  </AccordionTrigger>

                                  {/* Collapsed Preview — first 3-4 unit cards, rendered through
                                      the SAME VideoThumbnailCard component used in the expanded grid
                                      (parameterized via `compact`). No duplicated tile JSX. */}
                                  {!isOpen && (() => {
                                    const allAvailable = inventoryItem.serializedUnits?.filter((unit: any) =>
                                      !unit.isShipped && !unit.allocatedOrderItemId && !cartItems.some(item => item.unitId === unit.id)
                                    ) || [];
                                    const previewUnits = allAvailable.slice(0, 4);
                                    if (previewUnits.length === 0) return null;
                                    const overflow = allAvailable.length - previewUnits.length;
                                    return (
                                      <div
                                        className="px-3 pb-3 pt-1 cursor-pointer hover:bg-secondary/30"
                                        onClick={() => setOpenGrades([...openGrades, gradeAccordionValue])}
                                        data-testid={`preview-strip-${inventoryItem.id}`}
                                      >
                                        <div className="flex gap-2 overflow-hidden">
                                          {previewUnits.map((unit: any) => (
                                            <div key={unit.id} className="w-16 shrink-0">
                                              <VideoThumbnailCard
                                                serialNumber={unit.serialNumber}
                                                videoUrl={unit.videoUrl}
                                                thumbnailUrl={unit.thumbnailUrl}
                                                noVideoExpected={unit.noVideoExpected}
                                                grade={inventoryItem.grade}
                                                compact
                                              />
                                            </div>
                                          ))}
                                          {overflow > 0 && (
                                            <div className="w-16 h-[72px] rounded-lg border border-dashed border-border bg-muted/50 shrink-0 flex items-center justify-center text-xs text-muted-foreground font-medium">
                                              +{overflow}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                    <AccordionContent className="px-0 pb-0">
                      <div className="p-3">
                        {/* Serial mode section - filter out units in cart */}
                        {((() => {
                            const availableUnits = inventoryItem.serializedUnits?.filter((unit: any) => 
                              !unit.isShipped && !unit.allocatedOrderItemId && !cartItems.some(item => item.unitId === unit.id)
                            ) || [];

                            // gradeComingSoonItems already computed above (with in-cart presale items filtered out)

                            if (availableUnits.length === 0 && gradeComingSoonItems.length === 0 && inventoryItem.serializedUnits?.length > 0) {
                              // All units are in cart - show blue message
                              return (
                                <div className="text-center py-3 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                                  <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                    All available units in your cart
                                  </p>
                                </div>
                              );
                            }

                            // Unified card grid: every available unit + every coming-soon
                            // item renders through the same VideoThumbnailCard, which
                            // already handles the no-video placeholder + ship-date banner.
                            const sortedUnits = [...availableUnits].sort(
                              (a: any, b: any) => new Date(a.firstSeenDate).getTime() - new Date(b.firstSeenDate).getTime()
                            );

                            // If every batch is en-route (no 'in-stock' batch exists), all
                            // units are pre-arrival — show the ETA banner on each card.
                            // For mixed groups (some located, some en-route) we can't
                            // distinguish per-unit without location data, so we skip the banner.
                            const itemBatches = (inventoryItem.batches || []) as Array<{ key: string; eta: string | null }>;
                            const allEnRoute = itemBatches.length > 0 && itemBatches.every((b: any) => b.key !== 'in-stock');
                            const unitArrivalDate: string | null = allEnRoute
                              ? (itemBatches.find((b: any) => b.eta)?.eta ?? inventoryItem.eta ?? null)
                              : null;

                            return (
                              <div data-testid={`grade-b-section-${inventoryItem.id}`}>
                                {(sortedUnits.length > 0 || gradeComingSoonItems.length > 0) && (
                                  <div
                                    className="grid gap-3"
                                    style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
                                    data-testid={`unit-grid-${inventoryItem.id}`}
                                  >
                                    {sortedUnits.map((unit: any) => (
                                      <div key={unit.id} data-testid={`unit-card-${unit.id}`}>
                                        <VideoThumbnailCard
                                          serialNumber={unit.serialNumber}
                                          videoUrl={unit.videoUrl}
                                          thumbnailUrl={unit.thumbnailUrl}
                                          noVideoExpected={unit.noVideoExpected}
                                          grade={inventoryItem.grade}
                                          notes={unit.notes}
                                          shipDate={unitArrivalDate}
                                          colorSlot={unitArrivalDate ? getShipDateColor(unitArrivalDate?.slice(0, 10) ?? null, shipDateColorMap) : undefined}
                                          onClick={() => { if (unit.videoUrl) handleSelectUnit(unit, inventoryItem); }}
                                          onAddToCart={() => handleAddToCart(inventoryItem, unit)}
                                        />
                                      </div>
                                    ))}
                                    {gradeComingSoonItems.map((item) => {
                                      const colorSlot = getShipDateColor(item.expectedShipDate, shipDateColorMap);
                                      return (
                                        <div key={item.id} data-testid={`coming-soon-unit-${item.id}`}>
                                          <VideoThumbnailCard
                                            serialNumber={item.serialNumber}
                                            videoUrl={item.videoUrl}
                                            grade={item.grade}
                                            shipDate={item.expectedShipDate}
                                            colorSlot={colorSlot}
                                            onClick={() => {
                                              if (item.videoUrl) {
                                                handleSelectUnit(
                                                  { id: item.id, serialNumber: item.serialNumber, videoUrl: item.videoUrl, notes: null, thumbnailUrl: null },
                                                  inventoryItem
                                                );
                                              }
                                            }}
                                            onAddToCart={item.serializedUnitId ? () => handleAddToCartPresale(item, inventoryItem) : undefined}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })())}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                                </Accordion>
                              </div>
                            );
                          })}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* For virtual pallets: render "What's in this pallet" AFTER the
              add-to-cart / warehouse area so the buy CTA stays above the
              fold. */}
          {model.isPallet && (
            <div className="mb-4">
              <PalletCompositionCard palletModelId={model.id} />
            </div>
          )}

          {/* Fallback Coming Soon Section - for items that don't match any existing inventory row */}
          {(() => {
            // Collect all warehouse+grade combinations that have been displayed
            const displayedCombos = new Set<string>();
            visibleWarehouses.forEach(({ warehouseInventory }) => {
              warehouseInventory.forEach((inv: any) => {
                const wCode = inv.address?.warehouseCode || '';
                displayedCombos.add(`${wCode}:${inv.grade}`);
              });
            });
            hiddenWarehouses.forEach(({ warehouseInventory }) => {
              warehouseInventory.forEach((inv: any) => {
                const wCode = inv.address?.warehouseCode || '';
                displayedCombos.add(`${wCode}:${inv.grade}`);
              });
            });

            // Find Coming Soon items that weren't shown in any inventory section,
            // excluding any that are already in the cart
            const unmatchedComingSoon = comingSoonItems.filter((item: any) => {
              const comboKey = `${item.warehouseCode || ''}:${item.grade}`;
              return !displayedCombos.has(comboKey) &&
                !(item.serializedUnitId && presaleUnitIdsInCart.has(item.serializedUnitId));
            });

            if (unmatchedComingSoon.length === 0) return null;

            return (
              <Card className="overflow-hidden mb-3 border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20" data-testid="coming-soon-fallback-card">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-amber-800 dark:text-amber-200">Coming Soon</span>
                    <Badge variant="outline" className="ml-auto text-amber-700 dark:text-amber-300 border-amber-400">
                      {unmatchedComingSoon.length} unit{unmatchedComingSoon.length !== 1 ? 's' : ''}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                    Pre-arrival inventory not yet in our warehouse. Available to order now.
                  </p>
                  <div
                    className="grid gap-3"
                    style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
                    data-testid="unmatched-coming-soon-grid"
                  >
                    {unmatchedComingSoon.map((item) => {
                      const colorSlot = getShipDateColor(item.expectedShipDate, shipDateColorMap);
                      return (
                        <VideoThumbnailCard
                          key={item.id}
                          serialNumber={item.serialNumber}
                          videoUrl={item.videoUrl}
                          grade={item.grade}
                          shipDate={item.expectedShipDate}
                          colorSlot={colorSlot}
                          onClick={() => {
                            if (item.videoUrl) {
                              handleSelectUnit(
                                { id: item.id, serialNumber: item.serialNumber, videoUrl: item.videoUrl, notes: null, thumbnailUrl: null },
                                { grade: item.grade, id: '', price: 0 }
                              );
                            }
                          }}
                          onAddToCart={item.serializedUnitId ? () => handleAddToCartPresale(item) : undefined}
                        />
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Collapsible Filtered Items Section */}
          {filteredOutCount > 0 && (
            <Card className="overflow-hidden mb-3" data-testid="filtered-items-card">
              <CardContent className="p-0">
                <Collapsible className="border-t border-border" data-testid="filtered-items-section">
                  <CollapsibleTrigger className="w-full px-4 py-3 bg-muted/50 hover:bg-muted text-sm font-medium flex items-center justify-between" data-testid="button-toggle-filtered-items">
                    <span>Show {filteredOutCount} filtered item{filteredOutCount !== 1 ? 's' : ''}</span>
                    <Filter className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {hiddenWarehouses.map(({ warehouseKey, warehouseInventory, isInCart, firstItem }) => {
                      const warehouseCode = firstItem.address?.warehouseCode || 'N/A';
                      const city = firstItem.address?.city || '';
                      const region = firstItem.address?.region || '';
                      const warehouseLabel = firstItem.address 
                        ? `${warehouseCode} | ${city}, ${region}`
                        : 'Warehouse Not Assigned';

                      return (
                        <div 
                          key={`filtered-${warehouseKey}`}
                          className="border-b border-border last:border-b-0"
                        >
                          <div 
                            className="px-4 py-3 bg-secondary/30 font-semibold text-foreground flex items-center justify-between"
                            data-testid={`filtered-warehouse-section-${warehouseCode}`}
                          >
                            <span>{warehouseLabel}</span>
                            {!activeFilters.warehouseCodes.includes(warehouseCode) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs gap-1"
                                onClick={() => addFilter('warehouse', warehouseCode)}
                                data-testid={`button-add-filter-warehouse-${warehouseCode}`}
                              >
                                <MapPin className="h-3 w-3" />
                                Filter by this location
                              </Button>
                            )}
                          </div>
                          <div className="px-4 py-3 space-y-2">
                            {warehouseInventory.filter((inventoryItem: any) => !inventoryItem._isOutOfStock).map((inventoryItem: any) => {
                              return (
                                <div 
                                  key={inventoryItem.id} 
                                  className="flex items-center justify-between text-sm py-1 px-2 rounded"
                                  data-testid={`filtered-grade-${inventoryItem.id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <ConditionBadge isFactoryRefurbished={isFactoryRefurbished(model)} grade={inventoryItem.grade} price={parsePrice(inventoryItem.sellingPrice)} category={gradeBadgeCategory} />
                                    {!model.isPallet && (
                                      <span className="text-muted-foreground">Class {inventoryItem.grade}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="font-semibold">{formatMoney(parsePrice(inventoryItem.sellingPrice || '0'))}</span>
                                    <span className="text-muted-foreground text-xs">
                                      {inventoryItem.gradeHandlingMode === 'quantity' 
                                        ? `${inventoryItem._availableCount} available`
                                        : `${inventoryItem.serializedUnits?.filter((unit: any) => !unit.isShipped && !unit.allocatedOrderItemId && !cartItems.some(item => item.unitId === unit.id)).length || 0} available`
                                      }
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          )}

          {/* Mobile-only: Stock Notification & Parts Buttons - appear after inventory cards */}
          <div className="lg:hidden mt-3 space-y-2">
            <Button
              onClick={() => openNotificationDialogWithGrade('Any')}
              className="inline-flex items-center justify-center gap-2 rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 px-3 w-full hover:bg-[#45a049] text-white font-semibold py-4 h-auto whitespace-normal bg-[#9eba4a] text-sm touch-target"
              data-testid="button-stock-notification-mobile"
            >
              <Bell className="h-4 w-4 mr-1 flex-shrink-0" />
              Notify me for more stock
            </Button>

            <a
              href={`https://www.shopjimmy.com/search.php?search_query=${encodeURIComponent(model.brand + ' ' + model.mfgSku)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="link-view-parts-catalog-mobile"
            >
              <Button
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 px-3 w-full bg-[#546931] hover:bg-[#45a049] text-white font-semibold py-4 text-sm touch-target"
                data-testid="button-view-parts-catalog-mobile"
              >
                <Wrench className="h-4 w-4 mr-1 flex-shrink-0" />
                View Available Parts
              </Button>
            </a>
          </div>

          {/* Features / Specs / Docs — unified, icon-driven, tabbed when 2+ exist */}
          <ProductFeaturesAndSpecs
            features={model.features}
            specifications={model.specifications}
            showSpecifications={showSpecifications}
            relatedDocuments={model.relatedDocuments}
          />

          {/* Ordering Rules Card */}
          {((model.minOrderQty && model.minOrderQty > 1) || (model.orderMultiple && model.orderMultiple > 1) || model.unitsPerPalletLayer) && (
            <Card className="mb-3" data-testid="product-ordering-rules">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-600" />
                  Ordering Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2">
                  {model.minOrderQty && model.minOrderQty > 1 && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800" data-testid="ordering-rule-moq">
                      <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">Min. order qty:</span>
                      <span className="text-sm font-bold text-amber-900 dark:text-amber-200">{model.minOrderQty} units</span>
                    </div>
                  )}
                  {model.orderMultiple && model.orderMultiple > 1 && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800" data-testid="ordering-rule-multiple">
                      <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">Order in multiples of:</span>
                      <span className="text-sm font-bold text-amber-900 dark:text-amber-200">{model.orderMultiple}</span>
                    </div>
                  )}
                  {model.unitsPerPalletLayer && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700" data-testid="ordering-rule-pallet-layer">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Units per pallet layer:</span>
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{model.unitsPerPalletLayer}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Specifications now render inside the tabbed ProductFeaturesAndSpecs block above. */}

          {/* Warranty */}
          {model.warranty && (
            <Card className="mb-3" data-testid="product-warranty">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  Warranty Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <p className="text-sm text-foreground whitespace-pre-line">{model.warranty}</p>
              </CardContent>
            </Card>
          )}

          {/* Related Documents now render inside the tabbed ProductFeaturesAndSpecs block above. */}

          {/* Basic Specifications (fallback when no rich spec data) */}
          {showSpecifications && (!model.specifications ||
            ((!model.specifications.dimensions || model.specifications.dimensions.length === 0) &&
             (!model.specifications.details || model.specifications.details.length === 0))) && (
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-base">Specifications</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Brand</p>
                    <p className="text-sm font-medium text-foreground" data-testid="spec-brand">{displayBrand}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Model</p>
                    <p className="text-sm font-medium text-foreground">{model.mfgSku}</p>
                  </div>
                  {model.dimensionsWidth && model.dimensionsDepth && (
                    <div>
                      <p className="text-xs text-muted-foreground">Dimensions</p>
                      <p className="text-sm font-medium text-foreground">
                        {model.dimensionsWidth}" W × {model.dimensionsDepth}" D
                      </p>
                    </div>
                  )}
                  {model.msrp && (
                    <div>
                      <p className="text-xs text-muted-foreground">{referencePriceLabel}</p>
                      <p className="text-sm font-medium text-foreground">{formatMoney(parsePrice(model.msrp))}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT COLUMN: Shopping Cart - Mobile: order-3 (after warehouse cards), Desktop: normal */}
        <div className="lg:col-span-4 order-3 lg:order-none">
          {isModelInCart ? (
            <Card className="mb-4 bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700" data-testid="in-cart-indicator">
              <CardContent className="p-3">
                <p className="text-sm text-black dark:text-black text-center font-semibold">
                  {cartItems
                    .filter(item => item.modelId === modelId)
                    .reduce((sum, item) => sum + item.quantity, 0)} {displayBrand} {model?.mfgSku} in cart
                </p>
              </CardContent>
            </Card>
          ) : null}
          <ShoppingCart />
        </div>

      </div>

      {/* Video Modal */}
      <Dialog open={isVideoModalOpen} onOpenChange={(open) => {
        setIsVideoModalOpen(open);
        if (!open) setSelectedUnit(null);
      }}>
        <DialogContent className="max-w-4xl" data-testid="video-modal">
          <DialogHeader>
            <DialogTitle>
              {selectedUnit?.serialNumber} - {selectedUnit?.grade} Grade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <VideoPlayer
              src={selectedUnit?.videoUrl || ''}
              className="w-full aspect-video"
              poster={model?.imageUrl}
              autoPlay={true}
            />
            <Card className="bg-secondary/50">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Serial Number</p>
                    <p className="font-semibold text-foreground">
                      {selectedUnit?.serialNumber}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Price</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatMoney(parsePrice(inventory?.find((inv: any) => inv.id === selectedUnit?.inventoryId)?.sellingPrice || '0'))}
                    </p>
                  </div>
                </div>
                {selectedUnit?.notes && (
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground mb-1">Unit Notes</p>
                    <p className="text-sm text-foreground">{selectedUnit.notes}</p>
                  </div>
                )}
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => {
                    const inventoryItem = inventory?.find((inv: any) => inv.id === selectedUnit?.inventoryId);
                    if (inventoryItem) {
                      handleAddToCart(inventoryItem, selectedUnit);
                      setIsVideoModalOpen(false);
                    }
                  }}
                  data-testid="button-add-unit-to-cart-modal"
                >
                  Add This Unit to Cart
                </Button>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Notification Dialog */}
      <StockNotificationDialog
        isOpen={showStockNotificationDialog}
        onClose={() => setShowStockNotificationDialog(false)}
        modelId={modelId || ''}
        modelName={`${model?.brand} ${model?.mfgSku}` || ''}
        isAuthenticated={isAuthenticated}
        userId={user?.id}
        defaultGrade={notificationDefaultGrade}
      />
    </div>
    </section>
  );
}

// Stock Notification Dialog Component
function StockNotificationDialog({
  isOpen,
  onClose,
  modelId,
  modelName,
  isAuthenticated,
  userId,
  defaultGrade = 'Any',
}: {
  isOpen: boolean;
  onClose: () => void;
  modelId: string;
  modelName: string;
  isAuthenticated: boolean;
  userId?: string;
  defaultGrade?: string;
}) {
  const [requestedGrade, setRequestedGrade] = useState<string>(defaultGrade);
  const [requestedQuantity, setRequestedQuantity] = useState<number>(30);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Reset values when dialog opens with new default grade
  useEffect(() => {
    if (isOpen) {
      setRequestedGrade(defaultGrade);
      setRequestedQuantity(30);
    }
  }, [isOpen, defaultGrade]);

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to request stock notifications.",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    try {
      await apiRequest('POST', '/api/stock-notifications', {
        modelId,
        requestedGrade,
        requestedQuantity,
      });

      toast({
        title: "Notification Requested",
        description: `We'll notify you when ${requestedQuantity} ${modelName} (Grade ${requestedGrade}) is in stock.`,
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create stock notification request.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="stock-notification-dialog">
        <DialogHeader>
          <DialogTitle>Stock Notification Request</DialogTitle>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please log in to request stock notifications.
            </p>
            <Button
              onClick={() => {
                navigate('/login');
                onClose();
              }}
              className="w-full"
              data-testid="button-login-prompt"
            >
              Log In
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We'll notify you when <strong style={{color: '#9eba4a'}}>{modelName}</strong> is back in stock with your preferred grade and quantity.
            </p>

            <div className="space-y-2">
              <label className="text-sm font-medium">Requested Grade</label>
              <select
                value={requestedGrade}
                onChange={(e) => setRequestedGrade(e.target.value)}
                className="w-full p-2 border rounded-md"
                data-testid="select-requested-grade"
              >
                <option value="Any">Any Grade</option>
                <option value="A">Grade A</option>
                <option value="B">Grade B</option>
                <option value="B+">Grade B+</option>
                <option value="B-">Grade B-</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Requested Quantity</label>
              <input
                type="number"
                min="1"
                max="100"
                value={requestedQuantity}
                onChange={(e) => setRequestedQuantity(Math.min(100, parseInt(e.target.value) || 1))}
                className="w-full p-2 border rounded-md"
                data-testid="input-requested-quantity"
              />
            </div>

            <Button
              onClick={handleSubmit}
              className="w-full"
              data-testid="button-submit-notification"
            >
              Notify Me
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}